import { Plus, Trash2 } from 'lucide-react'
import Select from '@/components/ui/Select'
import { useFieldDefinitions } from '@/features/contacts/useContacts'
import { useMembers } from '@/features/team/useTeam'
import { useAgencyQuery } from '@/features/agency/useAgency'
import { useAuthStore } from '@/features/auth/authStore'
import { useFormsIf } from '@/features/forms/useForms'
import {
  CORE_FIELDS,
  CUSTOM_PREFIX,
  customKey,
  isBooleanField,
  isCustomField,
  opLabel,
  opNeedsValue,
  emptyCriterion,
  opsFor,
} from '@/features/lists/criteria'
import type { ListCriterion } from '@/api/lists'

const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/**
 * The filters of a dynamic list, one row per condition joined by AND — field, operator, value — with
 * value pickers that know the field (team members for Owner, the agency's lead sources, a custom field's
 * own choices) so nobody has to type an id or guess a spelling.
 */
export default function CriteriaEditor({
  criteria,
  onChange,
}: {
  criteria: ListCriterion[]
  onChange: (criteria: ListCriterion[]) => void
}) {
  const { data: members } = useMembers()
  const { data: contactFields } = useFieldDefinitions('contact')
  const { data: agency } = useAgencyQuery()
  const leadSources = agency?.leadSourceOptions ?? []
  // Forms are admin-managed; an agent's list editor just doesn't offer the "filled in a form" filter.
  const isAdmin = useAuthStore((s) => s.user?.role === 'AGENCY_ADMIN' || s.user?.role === 'PLATFORM_ADMIN')
  const { data: forms } = useFormsIf(isAdmin)

  const patch = (i: number, p: Partial<ListCriterion>) =>
    onChange(criteria.map((c, j) => (j === i ? { ...c, ...p } : c)))

  function onFieldChange(i: number, selected: string) {
    if (selected === '__custom') {
      patch(i, { field: CUSTOM_PREFIX + (contactFields?.[0]?.fieldKey ?? ''), op: 'eq', value: '' })
    } else {
      patch(i, { field: selected, op: opsFor(selected)[0], value: '' })
    }
  }

  function valueInput(c: ListCriterion, i: number) {
    const value = String(c.value ?? '')
    if (c.field === 'submitted_form') {
      return (
        <Select size="md" value={value} onChange={(e) => patch(i, { value: e.target.value })} className="w-full" aria-label="Form">
          <option value="">Choose a form…</option>
          {(forms ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </Select>
      )
    }
    if (c.field === 'owner_user_id') {
      return (
        <Select size="md" value={value} onChange={(e) => patch(i, { value: e.target.value })} className="w-full">
          <option value="">Choose a person…</option>
          {(members ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.fullName}
            </option>
          ))}
        </Select>
      )
    }
    if (isBooleanField(c.field)) {
      return (
        <Select size="md" value={value} onChange={(e) => patch(i, { value: e.target.value })} className="w-full">
          <option value="">Choose…</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </Select>
      )
    }
    const def = isCustomField(c.field) ? contactFields?.find((f) => f.fieldKey === customKey(c.field)) : undefined
    if (def?.options?.length && c.op !== 'contains') {
      return (
        <Select size="md" value={value} onChange={(e) => patch(i, { value: e.target.value })} className="w-full">
          <option value="">Choose…</option>
          {def.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label ?? o.value}
            </option>
          ))}
        </Select>
      )
    }
    const listId = c.field === 'lead_source' && leadSources.length ? `lead-sources-${i}` : undefined
    return (
      <>
        <input
          className={inputCls}
          placeholder="Value"
          value={value}
          list={listId}
          onChange={(e) => patch(i, { value: e.target.value })}
        />
        {listId && (
          <datalist id={listId}>
            {leadSources.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        )}
      </>
    )
  }

  return (
    <div className="space-y-2">
      {criteria.map((c, i) => {
        const ops = opsFor(c.field)
        return (
          <div key={i}>
            {i > 0 && <div className="text-[11px] font-semibold text-gray-400 tracking-wider text-center py-1">AND</div>}
            <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 grid gap-2 sm:grid-cols-[1fr_auto_1fr_auto] items-center">
              <div className="flex gap-2 min-w-0">
                <Select
                  size="md"
                  aria-label="Field"
                  value={isCustomField(c.field) ? '__custom' : c.field}
                  onChange={(e) => onFieldChange(i, e.target.value)}
                  className="w-full"
                >
                  {CORE_FIELDS.filter((f) => isAdmin || f.value !== 'submitted_form' || c.field === f.value).map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                  <option value="__custom">Custom field…</option>
                </Select>
                {isCustomField(c.field) && (contactFields ?? []).length > 0 && (
                  <Select
                    size="md"
                    aria-label="Custom field"
                    value={customKey(c.field)}
                    onChange={(e) => patch(i, { field: CUSTOM_PREFIX + e.target.value, value: '' })}
                    className="w-full"
                  >
                    {(contactFields ?? []).map((f) => (
                      <option key={f.fieldKey} value={f.fieldKey}>
                        {f.label}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
              <Select
                size="md"
                aria-label="Condition"
                value={c.op}
                onChange={(e) => patch(i, { op: e.target.value, value: opNeedsValue(e.target.value) ? c.value : undefined })}
              >
                {(ops.includes(c.op) ? ops : [c.op, ...ops]).map((o) => (
                  <option key={o} value={o}>
                    {opLabel(o, c.field)}
                  </option>
                ))}
              </Select>
              <div className="min-w-0">{opNeedsValue(c.op) ? valueInput(c, i) : null}</div>
              <button
                type="button"
                onClick={() => onChange(criteria.filter((_, j) => j !== i))}
                className="justify-self-end text-gray-400 hover:text-danger p-1"
                title="Remove this filter"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        )
      })}
      <button
        type="button"
        onClick={() => onChange([...criteria, emptyCriterion()])}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-primary hover:text-brand-dark"
      >
        <Plus size={15} /> Add filter
      </button>
    </div>
  )
}
